import React from 'react';
import { Dialog, Button, Flex, Text, useToasts } from '@applyboard/crystal-ui';
import { DeleteOutlineIcon } from '@applyboard/ui-icons';
import { useUpdateEducationHistoryCourses } from '~/data/useEducationHistoryCourses';
import { useAppProcessing } from '~/pages/ApplicationDetails/context';
import type { CourseEditPayload } from './index';

/**
 * Represents a course with flattened data structure for display purposes.
 * Includes course fields plus metadata like confidence and manual edit flag.
 */
export type FlattenedCourse = {
  id: string;
  courseName?: string;
  courseCode?: string;
  semester?: string;
  credits?: string;
  grade?: string;
  marksObtained?: string;
  results?: string;
  minimum?: string;
  maximum?: string;
  confidence?: 'HIGH' | 'MEDIUM' | 'LOW' | 'FAILED' | 'MANUAL';
  manuallyEdited?: boolean;
};

/**
 * Props for the DeleteCoursesDialog component.
 */
type DeleteCoursesDialogProps = {
  /** Unique identifier for the table/education history item (used for clearing selection) */
  tableKey: string;
  /** The transcript document ID to perform the deletion on */
  transcriptId: string;
  /** Array of courses selected for deletion */
  selectedCourses: FlattenedCourse[];
  /** Complete array of all courses (used to build the PATCH payload) */
  allCourses: CourseEditPayload[];
  /** Optional callback to clear checkbox selection after successful deletion */
  onClearSelection?: (key: string) => void;
  /** Optional callback to clear pending added courses after successful deletion */
  onClearPendingCourses?: (key: string) => void;
  /** Optional callback to execute after successful deletion (e.g., analytics) */
  onSuccess?: () => void;
  /** Optional callback to trigger GPA recalculation after courses are deleted */
  onRecalculate?: (educationHistoryKey: string) => void;
  /** Whether the delete button should be disabled */
  disabled?: boolean;
};

/**
 * A dialog component for bulk deletion of courses from an education history transcript.
 *
 * This component displays a delete button with a count of selected courses. When clicked,
 * it opens a confirmation dialog listing all selected courses. Upon confirmation, it marks
 * the selected courses as deleted via a PATCH request to the education history API.
 *
 * **Context Requirement:** Must be used within the ApplicationDetails context (`useAppProcessing`),
 * as it retrieves the student ID from the application data.
 *
 * **Behavior:**
 * - Retrieves `studentId` and `applicationId` from application context
 * - Uses the provided `transcriptId` to identify which education history record to update
 * - Marks selected courses as `deleted: true` in the PATCH request
 * - Mutation awaits cache invalidation and refetch (via hook's onSuccess), ensuring UI updates before dialog closes
 * - Shows success/error toasts
 * - Calls optional callbacks on success (`onClearSelection`, `onClearPendingCourses`, `onSuccess`)
 * - Automatically closes dialog on success, stays open on error
 *
 * @example
 * ```tsx
 * <DeleteCoursesDialog
 *   tableKey="student-123-transcript-456"
 *   transcriptId="eERhViLK6FKSawXGt9P07"
 *   selectedCourses={[course1, course2]}
 *   allCourses={[course1, course2, course3]}
 *   onClearSelection={(key) => clearSelection(key)}
 *   onClearPendingCourses={(key) => clearPendingCourses(key)}
 *   disabled={isEditing}
 * />
 * ```
 */
export const DeleteCoursesDialog: React.FC<DeleteCoursesDialogProps> = ({
  tableKey,
  transcriptId,
  selectedCourses,
  allCourses,
  onClearSelection,
  onClearPendingCourses,
  onSuccess,
  onRecalculate,
  disabled = false,
}) => {
  const count = selectedCourses.length;
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [isOpen, setIsOpen] = React.useState(false);
  const toast = useToasts();
  const { updateEducationHistoryCourses } = useUpdateEducationHistoryCourses();

  // Get application data from context
  const { application } = useAppProcessing();
  const studentId = application.studentPersonaInformation?.id;
  const applicationId = application.id;

  const handleConfirm = async () => {
    if (count === 0 || !studentId || !transcriptId || !applicationId) return;

    setIsDeleting(true);

    try {
      // Build courses array with deleted: true for selected courses
      const updatedCourses = allCourses.map((course) => {
        const isSelected = selectedCourses.some((sc) => sc.id === course.id);

        if (isSelected) {
          return {
            ...course,
            deleted: true, // Mark as deleted
          };
        }

        return course;
      });

      // Call PATCH endpoint and wait for cache invalidation & refetch to complete
      // The hook's onSuccess awaits the invalidateQueries call, ensuring UI is updated
      await updateEducationHistoryCourses({
        studentId,
        educationHistoryId: transcriptId,
        courses: updatedCourses,
        applicationId,
      });

      // Success: show toast and call callbacks
      toast.positive(`Successfully deleted ${count} course${count !== 1 ? 's' : ''}`);

      // Clear selection if callback provided
      if (onClearSelection) {
        onClearSelection(tableKey);
      }

      // Clear pending courses to prevent deleted courses from reappearing
      if (onClearPendingCourses) {
        onClearPendingCourses(tableKey);
      }

      // Call optional success callback
      if (onSuccess) {
        onSuccess();
      }

      if (onRecalculate) {
        onRecalculate(tableKey);
      }

      // Close dialog on success
      setIsOpen(false);
    } catch (error) {
      // Keep dialog open on error, show error toast
      toast.negative(new Error('Failed to delete courses. Please try again.'));
    } finally {
      setIsDeleting(false);
    }
  };

  // Check if we have all required data
  const canDelete = !!(studentId && transcriptId && applicationId);

  return (
    <Dialog size="md" open={isOpen} onOpenChange={setIsOpen}>
      <Dialog.Trigger>
        <Button
          leadIcon={DeleteOutlineIcon}
          intent="negative"
          emphasis="highlighted"
          size="md"
          disabled={disabled || count === 0 || !canDelete}
          aria-label={count > 0 ? `Delete ${count} selected course${count !== 1 ? 's' : ''}` : 'Delete'}
        >
          {`Delete${count > 0 ? ` (${count})` : ''}`}
        </Button>
      </Dialog.Trigger>

      <Dialog.Heading>Delete Courses</Dialog.Heading>

      <Dialog.Content>
        <Flex direction="column" gap={3}>
          <Text as="p">
            Are you sure you want to delete the following {count} course
            {count !== 1 ? 's' : ''}? This action cannot be undone.
          </Text>

          <div style={{ maxHeight: '300px', overflowY: 'auto', paddingLeft: '8px', paddingRight: '8px' }}>
            <Flex direction="column" gap={1}>
              {selectedCourses.map((course) => (
                <Text key={course.id} variant="bodyM">
                  • {course.courseName || 'Untitled Course'}
                  {course.courseCode ? ` (${course.courseCode})` : ''}
                </Text>
              ))}
            </Flex>
          </div>
        </Flex>
      </Dialog.Content>

      <Dialog.Actions>
        <Dialog.CloseButton width="fill" disabled={isDeleting}>
          Cancel
        </Dialog.CloseButton>
        <Button intent="negative" onClick={handleConfirm} width="fill" disabled={isDeleting} loading={isDeleting}>
          Delete
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};
